import { useState } from "react";
import Box from "../../components/Box";
import MainLayout from "../../components/layouts/MainLayout";
import AlertBox from "../../components/AlertBox";
import moment from "moment";
import { useReportMonthlyPayments } from "../../utils/finance/ReportBillingUtils";
import { convertToRupiah } from "../../tools/numerictools";
import { useControlPermission } from "../../utils/ActionPermissionUtils";
import { downloadReportMonthlyPayments } from "../../api/finance";
import MonthYearPicker from "../../components/MonthYearPicker";
import Pagination from "../../components/Pagination";

export default function ReportMonthlyPage() {
  const { hasAccess } = useControlPermission();
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  const [formFilter, setFormFilter] = useState({
    project_id: "",
    start_month: moment().format("M-YYYY"),
    end_month: "",
  });

  const [filter, setFilter] = useState({
    project_id: "",
    start_month: moment().format("M-YYYY"),
    end_month: "",
    page: 1,
    page_size: 12,
  });

  const { reports, meta, pagination, loading, errorMsg } = useReportMonthlyPayments({ filter });

  const applyFilter = () => {
    setFilter({
      ...filter,
      ...formFilter,
    });
  };

  const gotoPage = (page) => {
    setFilter({ ...filter, page: page });
  };

  const changeMonthYearFilter = (name, value) => {
    setFormFilter({ ...formFilter, [name]: value });
  };

  const onSuccess = (message) => {
    setAlert({ show: true, message, type: "success" });
  };

  const onError = (message) => {
    setAlert({ show: true, message, type: "danger" });
  };

  const resetFilter = () => {
    setFormFilter({
      project_id: "",
      start_month: moment().format("M-YYYY"),
      end_month: "",
    });

    setFilter({
      project_id: "",
      start_month: moment().format("M-YYYY"),
      end_month: "",
      page: 1,
      page_size: 12,
    });
  };

  return (
    <MainLayout resource_key="finance">
      <div className="container-fluid">
        <h1 className="h3 mb-4 text-gray-800">Laporan Keuangan Bulanan</h1>
        <Box title="Laporan Keuangan Bulanan" loading={loading}>
          <div className="row">
            <div className="col-12 col-md-2">
              <div className="form-group">
                <label>Bulan/Tahun mulai</label>
                <MonthYearPicker
                  value={formFilter.start_month}
                  changeMonthYear={(value) => changeMonthYearFilter("start_month", value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="form-group">
                <label>Bulan/Tahun Akhir</label>
                <MonthYearPicker
                  value={formFilter.end_month}
                  changeMonthYear={(value) => changeMonthYearFilter("end_month", value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-2">
              <button
                className="btn btn-primary"
                onClick={() => applyFilter()}
                style={{ marginTop: "32px" }}
              >
                Filter
              </button>
              <button
                className="btn btn-default"
                onClick={() => resetFilter()}
                style={{ marginTop: "32px" }}
              >
                Reset
              </button>
            </div>
            {hasAccess("export-monthly-customer-payment-report") && (
              <div className="col-12 col-md-auto ml-auto" style={{ paddingTop: "32px" }}>
                <ButtonDownloadReport filter={filter} onSuccess={onSuccess} onError={onError} />
              </div>
            )}
          </div>

          {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
          <AlertBox {...alert} setAlert={setAlert} />
          <table className="table table-bordered table-striped table-hover">
            <thead>
              <tr>
                <th>Bulan - Tahun</th>
                <th>Total Pendapatan</th>
                <th>Tanggal</th>
                <th>Pendapatan</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                return report.dates.map((revenue, indexY) => (
                  <tr key={indexY}>
                    {indexY === 0 ? (
                      <td rowSpan={report.dates.length} className="font-weight-bold">
                        {report.month}
                      </td>
                    ) : null}
                    {indexY === 0 ? (
                      <td
                        rowSpan={report.dates.length}
                        className="text-nowrap font-weight-bold text-right"
                      >
                        Rp {convertToRupiah(report.revenue)}
                      </td>
                    ) : null}
                    <td>{Object.keys(revenue)[0]}</td>
                    <td className="text-nowrap text-right">
                      Rp {convertToRupiah(revenue[Object.keys(revenue)[0]] || 0)}
                    </td>
                  </tr>
                ));
              })}
            </tbody>
            {/* <tfoot>
              <tr>
                <td className="font-weight-bold text-right" colSpan={3}>
                  Total
                </td>
                <td className="font-weight-bold text-right">
                  Rp {convertToRupiah(meta.total_revenue || 0)}
                </td>
              </tr>
            </tfoot> */}
          </table>

          <div className="row">
            <div className="col-12 col-lg text-lg d-flex align-items-center" style={{ gap: "4px" }}>
              Total Pendapatan <span>:</span>
              <b>Rp {convertToRupiah(meta.total_revenue || 0)}</b>
            </div>
            <div className="col-12 col-lg-auto">
              {pagination.count > pagination.page_size && (
                <Pagination
                  totalpage={Math.ceil(pagination.count / pagination.page_size)}
                  selectedpage={pagination.page}
                  clickpage={(page) => gotoPage(page)}
                  next={() => gotoPage(pagination.page + 1)}
                  prev={() => gotoPage(pagination.page - 1)}
                />
              )}
            </div>
          </div>
        </Box>
      </div>
    </MainLayout>
  );
}

function ButtonDownloadReport({ filter, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);

  const downloadReport = async () => {
    setLoading(true);
    try {
      let response = await downloadReportMonthlyPayments(filter);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Laporan Pembayaran per perbulan ${filter.start_month}.xlsx`);
      onSuccess(`Laporan Pembayaran per perbulan ${filter.start_month}.xlsx`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      let message = "Something wrong ... ";

      if (error.response) {
        let { data } = error.response;
        let responseObj = await data.text();
        let responseData = JSON.parse(responseObj);
        message = responseData?.message;
      } else {
        message = "Something Error Happened";
      }

      onError(message);
    }

    setLoading(false);
  };

  return (
    <button className="btn btn-primary" onClick={() => downloadReport()} disabled={loading}>
      <i className="fas fa-download"></i> {!loading ? "Unduh Data" : "Harap Tunggu ..."}
    </button>
  );
}
